import { cn } from '@/lib/utils'
import { ARTIFACTS_LABEL } from './accountModel'
import { AgentNameProvider } from './agentName'
import caiAd from './caiAd.webp'
import caiAdCfo from './caiAdCfo.webp'
import caiAdDrive from './caiAdDrive.webp'
import caiAdSurprises from './caiAdSurprises.webp'
import { CAI_AS_CFO_THREAD } from './contacts'
import { NavVariants } from './NavVariants'
import { DESKTOP_WIDTH, OptionSection } from './OptionSection'
import { PositioningNotes } from './PositioningNotes'
import { ProductOnboardingDesktop } from './ProductOnboardingDesktop'
import { ProductTab } from './prototypeNav'

const AGENT_GROUP = 'Cai is an agent'
const CFO_GROUP = 'Cai is a CFO'

/** The argument beside the line-up where the agent has absorbed the product. */
const CAI_AS_CFO_NOTES = [
  {
    items: [
      'It does some tasks for you and you’re limited but when you fully subscribe, it then becomes a fully unleashed CFO product.',
      'Position CFO like a capability or upgrade instead of a tab.',
    ],
  },
]

/** Ways the same account could name its tabs, for comparing the wording. */
const NAV_VARIANTS = [
  {
    label: 'Label by role or person',
    agent: 'Cai',
    tabs: ['Insurance Broker', 'Bookkeeper', 'CFO', 'Safety', 'Procurement'],
    pros: ['Anchors the user on the cost of hiring that person, when considering the cost of Cai.'],
    cons: [
      'Triggers the “AI replacing humans” narrative.',
      'Instead of 1 "who" and 5 "whats", reads like 6 agents.',
    ],
  },
  {
    label: 'Label by function',
    agent: 'Cai',
    tabs: ['Insurance', 'Bookkeeping', 'Finances', 'Compliance', 'Fuel'],
    pros: [
      'Less threatening. Large fleets more readily invite team members who occupy those roles.',
      'Similar to Claude Design, Claude Code (not Claude Designer, Claude Engineer)',
      'Reads like 1 "who" and 5 "whats". Cai is clearly the agent you talk to about these 5 domains.',
    ],
    cons: [],
  },
]

/** Nothing has named the agent yet, so it answers to the brand. */
const AGENT_NAME = 'Cai'

/** The account with the agent and nothing else bought: every product is an offer. */
export function OnlyCaiActiveSection({ shellClass }: { shellClass: string }) {
  return (
    <AgentNameProvider name={AGENT_NAME} artifactsLabel={ARTIFACTS_LABEL}>
      <OptionSection
        title="Cai positioning comparison"
        defaultOpen
        panes={[
          {
            label: 'Ad',
            group: AGENT_GROUP,
            width: 636,
            hideWidth: true,
            content: (
              <div className={cn('flex gap-4', shellClass)}>
                <div className="flex min-w-0 flex-1 flex-col gap-4">
                  <img
                    src={caiAd}
                    alt="Cai watches the money, you watch the road."
                    className="min-h-0 flex-1 object-contain"
                  />
                  <img
                    src={caiAdSurprises}
                    alt="Run your fleet with less surprises."
                    className="min-h-0 flex-1 object-contain"
                  />
                </div>
                <div className="flex min-w-0 flex-1 flex-col">
                  <img
                    src={caiAdDrive}
                    alt="Drive the truck and the business."
                    className="w-full object-contain"
                  />
                </div>
              </div>
            ),
          },
          {
            label: 'Desktop',
            group: AGENT_GROUP,
            width: DESKTOP_WIDTH,
            content: <ProductOnboardingDesktop className={shellClass} />,
          },
          {
            label: 'Notes',
            group: AGENT_GROUP,
            width: 300,
            hideWidth: true,
            content: <PositioningNotes />,
          },
          { label: 'divider', width: 1, divider: true, content: null },
          {
            label: 'Ad',
            group: CFO_GROUP,
            width: 310,
            hideWidth: true,
            content: (
              <div className={cn('flex flex-col', shellClass)}>
                <img
                  src={caiAdCfo}
                  alt="Cai watches the money, you watch the road. The first AI CFO for fleet owners."
                  className="w-full"
                />
              </div>
            ),
          },
          {
            label: 'Desktop · no CFO',
            group: CFO_GROUP,
            width: DESKTOP_WIDTH,
            content: (
              <ProductOnboardingDesktop
                className={shellClass}
                tabs={[ProductTab.INSURANCE, ProductTab.BOOKKEEPER]}
                thread={CAI_AS_CFO_THREAD}
                agentTabLabel="Cai CFO"
              />
            ),
          },
          {
            label: 'Notes',
            group: CFO_GROUP,
            width: 300,
            hideWidth: true,
            content: <PositioningNotes groups={CAI_AS_CFO_NOTES} />,
          },
          {
            label: 'Naming the tabs',
            width: DESKTOP_WIDTH,
            hideWidth: true,
            startsGroup: true,
            content: <NavVariants variants={NAV_VARIANTS} />,
          },
        ]}
      />
    </AgentNameProvider>
  )
}
